import React from 'react';
import type { CompendiumItem } from './CompendiumData';
import { ItemIcon } from '../../common/ItemIcon';
import { PriceHistoryChart } from '../market/PriceHistoryChart';
import type { Resource, MarketItem } from '../../../types';
import { X, ArrowRight, Pickaxe, Sprout, Hammer, Scroll, DollarSign, Package, Coins } from 'lucide-react';

interface ItemDetailViewProps {
    item: CompendiumItem;
    marketData?: MarketItem;
    ownedCount: number;
    onClose: () => void;
    onNavigate: (itemId: Resource) => void;
}

export const ItemDetailView: React.FC<ItemDetailViewProps> = ({ item, marketData, ownedCount, onClose, onNavigate }) => {

    // Helper for icons based on type
    const getSourceIcon = (type: string) => {
        switch (type) {
            case 'mining': return <Pickaxe size={16} />;
            case 'farming': return <Sprout size={16} />;
            case 'crafting': return <Hammer size={16} />;
            case 'upgrade': return <Scroll size={16} />;
            default: return <Package size={16} />;
        }
    };

    return (
        <div className="compendium-detail-panel" style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Close Button - Now relative to panel */}
            <button onClick={onClose} className="close-btn-panel" style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                zIndex: 10,
                background: 'rgba(0,0,0,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--color-text-muted)'
            }}>
                <X size={20} />
            </button>

            <div className="compendium-content" style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                <div className="compendium-container-inner">

                    {/* Hero Section */}
                    <div className="item-hero" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', width: '100%' }}>
                            <div className="item-hero-icon" style={{ width: '80px', height: '80px' }}>
                                <ItemIcon id={item.id} size={56} />
                            </div>
                            <div className="item-hero-details" style={{ flex: 1 }}>
                                <h1 className="item-title" style={{ fontSize: '1.8rem' }}>{item.name}</h1>
                                <div className="item-badges">
                                    <span className="item-badge category">{item.category}</span>
                                    {item.subCategory && <span className="item-badge">{item.subCategory}</span>}
                                </div>
                            </div>
                        </div>

                        <div className="item-description" style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', lineHeight: '1.5' }}>
                            {item.description || "No specific description available."}
                        </div>

                        {item.skillRequirement && (
                            <div style={{ fontSize: '0.85rem', color: 'var(--color-accent)', fontWeight: 600 }}>
                                Requires {item.skillRequirement} {item.levelRequirement}
                            </div>
                        )}
                    </div>

                    {/* Compact Stats Grid */}
                    {/* Compact Stats Group */}
                    <div className="stat-group">
                        <div className="stat-segment">
                            <div className="label"><Package size={14} /> Owned</div>
                            <div className="value">
                                {ownedCount.toLocaleString()}
                            </div>
                        </div>
                        <div className="stat-segment">
                            <div className="label"><DollarSign size={14} /> Price</div>
                            <div className="value gold">
                                {marketData?.currentPrice?.toLocaleString() ?? 'N/A'} <span style={{ fontSize: '0.8em', opacity: 0.7 }}>g</span>
                            </div>
                        </div>
                        <div className="stat-segment">
                            <div className="label"><Coins size={14} /> Stack Value</div>
                            <div className="value gold">
                                {(ownedCount * (marketData?.currentPrice ?? 0)).toLocaleString()} <span style={{ fontSize: '0.8em', opacity: 0.7 }}>g</span>
                            </div>
                        </div>
                    </div>

                    {/* Sources & Uses Grid - Stacked for Panel */}
                    <div className="info-grid" style={{ gridTemplateColumns: '1fr', marginTop: '12px' }}>

                        {/* Sources (Acquisition) */}
                        <div className="info-section">
                            <h3 className="info-header"><ArrowRight size={18} style={{ transform: 'rotate(180deg)' }} /> Acquisition</h3>
                            {item.sources.length === 0 ? (
                                <div className="empty-state">No specific acquisition path.</div>
                            ) : (
                                <div className="info-list">
                                    {item.sources.map((s, i) => (
                                        <div key={i}
                                            className={`info-item ${s.id ? 'clickable' : ''}`}
                                            onClick={() => s.id ? onNavigate(s.id as Resource) : null}
                                        >
                                            <div className="info-icon">{getSourceIcon(s.type)}</div>
                                            <div className="info-details">
                                                <div className="info-title">{s.detail}</div>
                                                <div className="info-subtitle">{s.type}</div>
                                                {/* Recipe Ingredients */}
                                                {s.recipe && (
                                                    <div className="recipe-ingredients">
                                                        {Object.entries(s.recipe).map(([resId, amount]) => (
                                                            <div
                                                                key={resId}
                                                                className="recipe-ingredient clickable"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onNavigate(resId as Resource);
                                                                }}
                                                                title={`View ${resId.replace(/_/g, ' ')}`}
                                                            >
                                                                <ItemIcon id={resId} size={14} /> <span>{amount}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {s.id && <ArrowRight size={14} style={{ opacity: 0.3 }} />}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Uses (Usage) */}
                        <div className="info-section">
                            <h3 className="info-header"><ArrowRight size={18} /> Usage</h3>
                            {item.uses.length === 0 ? (
                                <div className="empty-state">No known uses.</div>
                            ) : (
                                <div className="info-list">
                                    {item.uses.map((u, i) => (
                                        <div key={i}
                                            className={`info-item ${u.id ? 'clickable' : ''}`}
                                            onClick={() => u.id ? onNavigate(u.id as Resource) : null}
                                        >
                                            <div className="info-icon">{getSourceIcon(u.type)}</div>
                                            <div className="info-details">
                                                <div className="info-title">{u.detail}</div>
                                                <div className="info-subtitle">{u.type}</div>
                                                {/* Recipe Ingredients (if applicable) */}
                                                {u.recipe && (
                                                    <div className="recipe-ingredients">
                                                        {Object.entries(u.recipe).map(([resId, amount]) => (
                                                            <div
                                                                key={resId}
                                                                className="recipe-ingredient clickable"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onNavigate(resId as Resource);
                                                                }}
                                                                title={`View ${resId.replace(/_/g, ' ')}`}
                                                            >
                                                                <ItemIcon id={resId} size={14} /> <span>{amount}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {u.id && <ArrowRight size={14} style={{ opacity: 0.3 }} />}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Chart Section */}
                    {marketData && (
                        <div className="market-chart-section" style={{ marginTop: '24px' }}>
                            <h3><DollarSign size={16} /> Price History</h3>
                            <div style={{ height: '160px' }}>
                                <PriceHistoryChart data={marketData.history} height={160} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
